/**
 * Intent Agent – Dubai Police 999 Emergency Assistant
 *
 * Listens to the user and classifies their intent WITHOUT answering.
 * Routes to inquiry_agent (with detected agencies), human_handoff_agent,
 * or followup_agent (when more info needed for confident classification).
 *
 * When returning from followup_agent (followupAnswers in context), uses
 * the gathered answers to make a final confident classification.
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';

export function createIntentAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  return {
    id: 'intent_agent',
    name: 'Intent Detection Agent',
    description: 'Classifies user intent and routes to inquiry, handoff, or followup — does NOT answer',

    initialPrompt: 'Listen to the user. Classify their intent — do NOT answer. If confident: call route_to_inquiry (with detected_agencies) or route_to_handoff. If genuinely ambiguous: call request_followup with 1-3 questions. If returning from follow-up with answers in context: use those answers to classify and route NOW.',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['to_inquiry',  { target: { agentId: 'inquiry_agent' },       guardrails: [languageDetectedGuardrail] }],
        ['to_crisis',   { target: { agentId: 'image_upload_agent' },  guardrails: [languageDetectedGuardrail] }],
        ['to_handoff',  { target: { agentId: 'human_handoff_agent' }, guardrails: [languageDetectedGuardrail] }],
        ['to_followup', { target: { agentId: 'followup_agent' },      guardrails: [languageDetectedGuardrail] }],
      ]),
      fallback: { agentId: 'inquiry_agent' },
    },

    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {

      // ─── route_to_crisis ────────────────────────────────────────────────
      if (toolName === 'route_to_crisis') {
        console.log(
          '%c[IntentAgent] 🚨 CRITICAL CRISIS detected — routing directly to image upload (all agencies auto-flagged)',
          'color: #ef4444; font-weight: bold'
        );
        return {
          toolName, args,
          result: { success: true, message: 'Critical crisis detected — skipping inquiry and agency consent, routing to image upload' },
          triggersTransition: true,
          transitionTo: 'image_upload_agent',
          transitionData: {
            isCrisis: true,
            recommendedAgencies: ['police', 'ambulance', 'civil_defense'],
            confirmedAgencies: context.transitionData.confirmedAgencies || [],
            followupAnswers: undefined,
            followupQuestions: undefined,
          },
        };
      }

      // ─── route_to_inquiry ───────────────────────────────────────────────
      if (toolName === 'route_to_inquiry') {
        const query: string = args.query || '';
        const detectedAgencies: string[] = args.detected_agencies || [];

        console.log(
          `%c[IntentAgent] Routing to inquiry | agencies: [${detectedAgencies.join(', ')}] | query: "${query.slice(0, 80)}"`,
          'color: #ff9800; font-weight: bold'
        );

        return {
          toolName, args,
          result: {
            success: true,
            message: `Routing to inquiry agent. Detected agencies: [${detectedAgencies.join(', ')}]`,
          },
          triggersTransition: true,
          transitionTo: 'inquiry_agent',
          transitionData: {
            pendingQuestion: query,
            recommendedAgencies: detectedAgencies,
            confirmedAgencies: context.transitionData.confirmedAgencies || [],
            // Clear followup data once used
            followupAnswers: undefined,
            followupQuestions: undefined,
          },
        };
      }

      // ─── route_to_handoff ───────────────────────────────────────────────
      if (toolName === 'route_to_handoff') {
        console.log('[IntentAgent] Routing to human handoff');
        return {
          toolName, args,
          result: { success: true, message: 'Routing to human handoff' },
          triggersTransition: true,
          transitionTo: 'human_handoff_agent',
          transitionData: {
            confirmedAgencies: context.transitionData.confirmedAgencies || [],
            followupAnswers: undefined,
            followupQuestions: undefined,
          },
        };
      }

      // ─── request_followup ───────────────────────────────────────────────
      if (toolName === 'request_followup') {
        const questions: string[] = args.questions || [];

        console.log(
          `%c[IntentAgent] Requesting follow-up | questions: [${questions.join(' | ')}]`,
          'color: #9c27b0; font-weight: bold'
        );

        return {
          toolName, args,
          result: {
            success: true,
            message: `Routing to follow-up agent with ${questions.length} question(s)`,
          },
          triggersTransition: true,
          transitionTo: 'followup_agent',
          transitionData: {
            // Pass questions to followup_agent
            followupQuestions: questions,
            // Preserve the original query if we have it
            pendingQuestion: context.transitionData.pendingQuestion || '',
            recommendedAgencies: context.transitionData.recommendedAgencies || [],
            confirmedAgencies: context.transitionData.confirmedAgencies || [],
          },
        };
      }

      return { toolName, args, result: { success: false, error: `Unknown tool: ${toolName}` } };
    },

    onActivate: async (context: AgentContext) => {
      const followupAnswers = (context.transitionData as any).followupAnswers;
      const pendingQuery = context.transitionData.pendingQuestion;

      if (followupAnswers && Object.keys(followupAnswers).length > 0) {
        console.log(
          '[IntentAgent] Activated with follow-up answers — will classify and route immediately.',
          followupAnswers
        );
      } else {
        console.log('[IntentAgent] Activated — waiting for user input. Pending query:', pendingQuery || '(none)');
      }
    },
  };
}

export default createIntentAgent;
